// ============================================================================
// Time & Labor — ONE way to get a report out of this app.
//
// ⛔ Ben, relaying HR, 2026-09-22: "Reports/exports should be Excel and PDF
// and CSV (in that order, with Excel as default)."
//
// So: Excel first and visually primary, PDF second (print, which is what
// "save as PDF" is on every browser and on every Mac), CSV last and quiet.
// CSV is kept because Gusto eats CSV and nothing else — it is a machine
// format that happens to open in a spreadsheet, not a spreadsheet.
//
// ⛔ EXCEL MEANS A REAL .xlsx, NOT A CSV WITH THE EXTENSION CHANGED. That
// trick makes Excel throw a format warning on every open, mangles any value
// it decides looks like a date, and drops leading zeros off an id. A real
// workbook types its own cells and Excel opens it silently.
// ============================================================================

// ⛔ THE EXCEL WRITER IS LOADED ON THE BUTTON PRESS, NOT ON PAGE LOAD.
// A static import puts the whole zip-and-XML writer into the main bundle that
// every worker's phone downloads to punch in, to serve four office screens
// that most people never open. The dynamic import below splits it into its own
// chunk, so the cost lands on whoever actually asks for a spreadsheet.
//
// ⛔ `/browser`, not the bare package name. write-excel-file publishes no root
// export: it ships a node build and a browser build behind separate subpath
// exports, and importing the bare name fails to resolve at all.

export type CellValue = string | number | null | undefined;

/** Quote a CSV cell the way Excel expects. */
function csvCell(v: CellValue): string {
  const s = v == null ? '' : String(v);
  return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

function saveBlob(filename: string, blob: Blob): void {
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}

/**
 * CSV — for a machine on the other end.
 *
 * ⛔ The leading BOM is load-bearing: without it Excel reads the file as the
 * local code page and every accented name in the roster comes out mojibake.
 */
export function downloadCsv(filename: string, rows: CellValue[][]): void {
  const text = '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n');
  saveBlob(filename, new Blob([text], { type: 'text/csv;charset=utf-8' }));
}

/**
 * Excel — a real workbook, with the first row frozen as a header.
 *
 * Takes the SAME `rows` shape as the CSV path on purpose: one array of arrays,
 * header first. Every caller therefore builds its report once and offers it in
 * both formats, and the two can never disagree about what is in it.
 *
 * ⛔ Numbers are written as numbers and everything else as text, so hours add
 * up in the sheet and a worker id like `WRK-0004` keeps its shape instead of
 * being guessed at.
 */
export async function downloadExcel(
  filename: string,
  sheetName: string,
  rows: CellValue[][],
  /** Optional Excel number format for every number cell, e.g. '0.000' for hours
   *  shown to three decimals. Omitted, numbers keep Excel's General format. */
  numberFormat?: string,
): Promise<void> {
  const { default: writeXlsxFile } = await import('write-excel-file/browser');
  const data = rows.map((row, r) => row.map((v) => {
    if (r === 0) return { value: v == null ? '' : String(v), fontWeight: 'bold' as const };
    if (typeof v === 'number' && Number.isFinite(v)) {
      return numberFormat ? { type: Number, value: v, format: numberFormat } : { type: Number, value: v };
    }
    return { type: String, value: v == null ? '' : String(v) };
  }));
  await writeXlsxFile(data, {
    // Excel caps a sheet name at 31 characters and refuses : \ / ? * [ ].
    sheet: sheetName.replace(/[:\\/?*[\]]/g, ' ').slice(0, 31) || 'Report',
    stickyRowsCount: 1,
  }).toFile(filename);
}

/**
 * PDF — the browser's own print dialogue, which is where "Save as PDF" lives.
 *
 * ⛔ No PDF library. Ben approved "print PDF" precisely so that what somebody
 * sends is what they were looking at, rendered by the thing that rendered it.
 * A second renderer is a second layout to keep in step with the screen, and it
 * drifts the first time anybody changes a column.
 */
export function printToPdf(): void {
  window.print();
}

/** The three formats, in the order HR asked for them. */
export const EXPORT_FORMATS = ['excel', 'pdf', 'csv'] as const;
export type ExportFormat = (typeof EXPORT_FORMATS)[number];
